<template>
  <div :data-theme="isDarkMode ? 'dark' : 'light'" class="app-wrapper">
    <div class="container">
      <!-- 헤더: 앱 타이틀 + 다크모드 토글 -->
      <header class="app-header">
        <h1 class="app-title">HowLongHowFast</h1>
        <button @click="toggleDarkMode" class="theme-toggle" aria-label="Toggle Dark Mode">
          <span v-if="isDarkMode">☀️</span>
          <span v-else>🌙</span>
        </button>
      </header>

      <!-- 카드 1: 계산 항목 선택 (세그먼트 컨트롤) -->
      <div class="card selector-card">
        <div class="card-label">구하고자 하는 항목</div>
        <div class="segmented-control">
          <button 
            v-for="option in options" 
            :key="option.value"
            :class="['segment-btn', { active: selectedOption === option.value }]"
            @click="selectedOption = option.value; calc()"
          >
            {{ option.label }}
          </button>
        </div>
      </div>

      <!-- 카드 2: 입력/결과 영역 -->
      <div class="card main-card">
        <div class="input-group" :class="{ 'result-highlight': selectedOption === 'distance' }">
          <label>거리 (km)</label>
          <div v-if="selectedOption !== 'distance'" class="distance-presets">
            <button 
              v-for="preset in distancePresets" 
              :key="preset.id"
              :class="['preset-btn', { active: selectedDistancePreset === preset.id }]"
              @click="applyDistancePreset(preset)"
            >
              {{ preset.label }}
            </button>
          </div>
          <div class="input-wrapper">
            <input 
              v-if="selectedOption !== 'distance'"
              v-model="distanceInput" 
              type="number" 
              step="0.001"
              placeholder="예: 42.195" 
              @input="onDist"
            />
            <div v-else class="result-value">{{ distanceInput }} km</div>
          </div>
        </div>

        <div class="input-group" :class="{ 'result-highlight': selectedOption === 'time' }">
          <label>시간 (hh:mm:ss)</label>
          <div class="input-wrapper">
            <input 
              v-if="selectedOption !== 'time'"
              v-model="timeInput" 
              placeholder="예: 3:30:00" 
              @input="onTime"
            />
            <div v-else class="result-value">{{ timeInput }}</div>
          </div>
        </div>

        <div class="input-group" :class="{ 'result-highlight': selectedOption === 'pace' }">
          <label>페이스 (mm:ss)</label>
          <div class="input-wrapper">
            <input 
              v-if="selectedOption !== 'pace'"
              v-model="paceInput" 
              placeholder="예: 6:30" 
              @input="onPace" 
              @blur="validatePaceFormat"
            />
            <div v-else class="result-value">{{ paceInput }}</div>
            <div v-if="paceValidationMessage" class="error-msg">{{ paceValidationMessage }}</div>
          </div>
        </div>
      </div>

      <!-- 카드 3: 케이던스 & 보폭 -->
      <div class="card detail-card">
        <p class="guide-text">
          케이던스와 보폭은 페이스를 기반으로 상호반비례 관계입니다.<br>
          본인의 신체조건과 목표에 맞게 최적값을 찾아보세요.
        </p>
        
        <div class="input-grid">
          <div class="input-group">
            <label>케이던스 (bpm)</label>
            <input v-model="cadenceInput" type="number" placeholder="180" @input="onCandence" />
          </div>
          <div class="input-group">
            <label>보폭 (meter)</label>
            <input v-model="footInput" type="number" step="0.01" placeholder="1.2" @input="onFoot" />
          </div>
        </div>
      </div>

      <!-- 저장 버튼 -->
      <button class="save-btn" @click="saveToHistory">기록 저장하기</button>

      <!-- 카드 4: 히스토리 -->
      <div class="card history-card" :class="{ expanded: isHistoryVisible }">
        <div class="history-header" @click="isHistoryVisible = !isHistoryVisible">
          <div class="card-label">저장된 히스토리 ({{ history.length }})</div>
          <span class="toggle-icon">{{ isHistoryVisible ? '▲' : '▼' }}</span>
        </div>
        
        <div v-if="isHistoryVisible" class="history-list">
          <div v-if="history.length === 0" class="empty-msg">저장된 기록이 없습니다.</div>
          <div v-for="item in history" :key="item.id" class="history-item" @click="loadFromHistory(item)">
            <div class="history-info">
              <span class="history-date">{{ item.date }}</span>
              <div class="history-values">
                <strong>{{ item.distance }}km</strong> | 
                <strong>{{ item.time }}</strong> | 
                <strong>{{ item.pace }}/km</strong>
              </div>
            </div>
            <button class="delete-btn" @click.stop="deleteFromHistory(item.id)">✕</button>
          </div>
        </div>
      </div>

      <footer class="app-footer">@eto2000</footer>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      selectedOption: "pace",
      distanceInput: "42.195",
      paceInput: "05:41",
      timeInput: "04:00:00",
      cadenceInput: 180,
      footInput: "1.2",
      paceValidationMessage: "",
      isUpdatedCadence: true,
      
      // New data for UI/UX
      isDarkMode: false,
      isHistoryVisible: false,
      history: [],
      options: [
        { label: '페이스', value: 'pace' },
        { label: '거리', value: 'distance' },
        { label: '시간', value: 'time' }
      ],
      selectedDistancePreset: 'full',
      distancePresets: [
        { id: 'full', label: '풀', value: 42.195 },
        { id: 'half', label: '하프', value: 21.0975 },
        { id: '10k', label: '10km', value: 10 },
        { id: '5k', label: '5km', value: 5 }
      ]
    };
  },
  mounted() {
    this.loadTheme();
    this.loadHistory();
    this.calc();
    
    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', (e) => {
      if (localStorage.getItem('hlhf_theme') === null) {
        this.isDarkMode = e.matches;
      }
    });
  },
  methods: {
    // Original Logic
    getSeconds() {
      const arr = this.timeInput.split(':');
      let hh = 0, mm = 0, ss = 0;
      if (arr.length === 3) {
        hh = Number(arr[0]) || 0;
        mm = Number(arr[1]) || 0;
        ss = Number(arr[2]) || 0;
      } else if (arr.length === 2) {
        hh = Number(arr[0]) || 0;
        mm = Number(arr[1]) || 0;
      } else if (arr.length === 1) {
        mm = Number(arr[0]) || 0;
      }
      return hh * 3600 + mm * 60 + ss;
    },
    pace2Sec() {
      const arr = this.paceInput.split(':');
      if (arr.length < 2) return 0;
      const mm = Number(arr[0]) || 0;
      const ss = Number(arr[1]) || 0;
      return mm * 60 + ss;
    },
    getSpeed() {
      const seconds = this.pace2Sec();
      return seconds > 0 ? 1 / seconds : 0;
    },
    calc() {
      switch (this.selectedOption) {
        case 'distance': {
          let secSpeed = this.getSpeed();
          let seconds = this.getSeconds();
          this.distanceInput = (Math.floor(secSpeed * seconds * 100) / 100).toFixed(3);
          break;
        }
        case 'pace': {
          if (this.distanceInput <= 0) break;
          let seconds = this.getSeconds() / this.distanceInput;
          const secondsToMinutesAndSeconds = (sec) => {
            var minutes = Math.floor(sec / 60);
            var remainingSeconds = Math.floor(sec % 60);
            minutes = (minutes < 10) ? '0' + minutes : minutes;
            remainingSeconds = (remainingSeconds < 10) ? '0' + remainingSeconds : remainingSeconds;
            return minutes + ':' + remainingSeconds;
          };
          this.paceInput = secondsToMinutesAndSeconds(seconds);
          break;
        }
        case 'time': {
          let secSpeed = this.getSpeed();
          if (secSpeed <= 0) break;
          const seconds = this.distanceInput / secSpeed;
          const secondsToHourMinSec = (sec) => {
            var hours = Math.floor(sec / 3600);
            var minutes = Math.floor((sec % 3600) / 60);
            var remainingSeconds = Math.floor(sec % 60);
            hours = (hours < 10) ? '0' + hours : hours;
            minutes = (minutes < 10) ? '0' + minutes : minutes;
            remainingSeconds = (remainingSeconds < 10) ? '0' + remainingSeconds : remainingSeconds;
            return hours + ':' + minutes + ':' + remainingSeconds;
          };
          this.timeInput = secondsToHourMinSec(seconds);
          break;
        }
      }

      // 케이던스 보폭 계산
      const speedInMpm = this.getSpeed() * 1000 * 60; // meter per minute
      if (this.isUpdatedCadence) {
        if (this.cadenceInput > 0) {
          this.footInput = (speedInMpm / this.cadenceInput).toFixed(2);
        }
      } else {
        if (this.footInput > 0) {
          this.cadenceInput = Math.round(speedInMpm / this.footInput);
        }
      }
    },
    onPace() { this.calc(); },
    onDist() {
      this.selectedDistancePreset = 'custom';
      this.calc();
    },
    applyDistancePreset(preset) {
      this.selectedDistancePreset = preset.id;
      if (preset.value !== null) {
        this.distanceInput = preset.value;
      }
      this.calc();
    },
    onTime() { this.calc(); },
    onCandence() {
      this.isUpdatedCadence = true;
      this.calc();
    },
    onFoot() {
      this.isUpdatedCadence = false;
      this.calc();
    },
    validatePaceFormat() {
      const paceRegex = /^\d{1,2}:\d{2}$/;
      if (!paceRegex.test(this.paceInput)) {
        this.paceValidationMessage = "올바른 형식이 아닙니다. (예: 6:30)";
      } else {
        this.paceValidationMessage = "";
      }
    },

    // UI/Storage Methods
    toggleDarkMode() {
      this.isDarkMode = !this.isDarkMode;
      localStorage.setItem('hlhf_theme', this.isDarkMode ? 'dark' : 'light');
    },
    loadTheme() {
      const savedTheme = localStorage.getItem('hlhf_theme');
      if (savedTheme) {
        this.isDarkMode = savedTheme === 'dark';
      } else {
        this.isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
      }
    },
    saveToHistory() {
      const now = new Date();
      const timestamp = now.getTime();
      const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      
      const newEntry = {
        id: timestamp,
        date: dateStr,
        selectedOption: this.selectedOption,
        distance: this.distanceInput,
        time: this.timeInput,
        pace: this.paceInput,
        cadence: this.cadenceInput,
        foot: this.footInput
      };
      
      this.history.unshift(newEntry);
      if (this.history.length > 50) this.history.pop();
      this.syncStorage();
      this.isHistoryVisible = true;
    },
    loadFromHistory(item) {
      this.selectedOption = item.selectedOption;
      this.distanceInput = item.distance;
      this.timeInput = item.time;
      this.paceInput = item.pace;
      this.cadenceInput = item.cadence;
      this.footInput = item.foot;
      this.calc();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    deleteFromHistory(id) {
      this.history = this.history.filter(item => item.id !== id);
      this.syncStorage();
    },
    syncStorage() {
      localStorage.setItem('hlhf_history', JSON.stringify(this.history));
    },
    loadHistory() {
      const saved = localStorage.getItem('hlhf_history');
      if (saved) {
        try {
          this.history = JSON.parse(saved);
        } catch (e) {
          this.history = [];
        }
      }
    }
  }
};
</script>

<style>
/* CSS Variables */
:root {
  --bg-primary: #f8f9fa;
  --bg-card: #ffffff;
  --bg-input: #f0f2f5;
  --text-primary: #1a1a2e;
  --text-secondary: #6c757d;
  --accent: #4361ee;
  --accent-hover: #3a56d4;
  --border: #dee2e6;
  --shadow: rgba(0, 0, 0, 0.08);
  --result-bg: #eef2ff;
  --error: #ef4444;
}

[data-theme="dark"] {
  --bg-primary: #1a1a2e;
  --bg-card: #16213e;
  --bg-input: #0f3460;
  --text-primary: #e8e8e8;
  --text-secondary: #a0a0b0;
  --accent: #4cc9f0;
  --accent-hover: #72d6f5;
  --border: #2a2a4a;
  --shadow: rgba(0, 0, 0, 0.3);
  --result-bg: #1a2744;
}

/* Base Styles */
* {
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
}

body {
  margin: 0;
  font-family: 'Pretendard', sans-serif;
  background-color: var(--bg-primary);
  color: var(--text-primary);
  transition: background-color 0.3s ease, color 0.3s ease;
}

.app-wrapper {
  min-height: 100vh;
  padding: 20px 0;
}

.container {
  max-width: 420px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Header */
.app-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.app-title {
  font-size: 1.5rem;
  font-weight: 800;
  margin: 0;
  background: linear-gradient(135deg, var(--accent), #7209b7);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.theme-toggle {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px var(--shadow);
  font-size: 1.2rem;
  transition: all 0.2s;
}

.theme-toggle:active {
  transform: scale(0.95);
}

/* Cards */
.card {
  background: var(--bg-card);
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 4px 12px var(--shadow);
  transition: background-color 0.3s ease;
}

.card-label {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text-secondary);
  margin-bottom: 12px;
}

/* Segmented Control */
.segmented-control {
  display: flex;
  background: var(--bg-input);
  border-radius: 12px;
  padding: 4px;
}

.segment-btn {
  flex: 1;
  border: none;
  background: transparent;
  padding: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-secondary);
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s;
}

.segment-btn.active {
  background: var(--bg-card);
  color: var(--accent);
  box-shadow: 0 2px 6px var(--shadow);
}

/* Distance Presets */
.distance-presets {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.preset-btn {
  flex: 1;
  min-width: 60px;
  background: var(--bg-input);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 6px 4px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
}

.preset-btn.active {
  background: var(--accent);
  color: white;
  border-color: var(--accent);
}

/* Input Styles */
.input-group {
  margin-bottom: 16px;
}

.input-group:last-child {
  margin-bottom: 0;
}

.input-group label {
  display: block;
  font-size: 0.85rem;
  margin-bottom: 6px;
  color: var(--text-secondary);
}

.input-wrapper {
  position: relative;
}

input {
  width: 100%;
  background: var(--bg-input);
  border: 2px solid transparent;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 1.1rem;
  font-family: inherit;
  color: var(--text-primary);
  text-align: right;
  transition: all 0.2s;
}

input:focus {
  outline: none;
  border-color: var(--accent);
  background: var(--bg-card);
}

.result-highlight {
  background: var(--result-bg);
  margin: 0 -20px;
  padding: 16px 20px;
}

.result-value {
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--accent);
  text-align: right;
}

.error-msg {
  color: var(--error);
  font-size: 0.75rem;
  margin-top: 4px;
}

/* Detail Card Grid */
.input-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.guide-text {
  font-size: 0.8rem;
  color: var(--text-secondary);
  line-height: 1.5;
  margin-top: 0;
  margin-bottom: 16px;
}

/* Buttons */
.save-btn {
  width: 100%;
  background: var(--accent);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 14px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  margin-bottom: 24px;
  box-shadow: 0 4px 12px rgba(67, 97, 238, 0.3);
  transition: all 0.2s;
}

.save-btn:active {
  transform: translateY(2px);
  box-shadow: 0 2px 6px rgba(67, 97, 238, 0.3);
}

/* History Card */
.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
}

.history-header .card-label {
  margin-bottom: 0;
}

.history-list {
  margin-top: 16px;
  max-height: 300px;
  overflow-y: auto;
}

.history-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid var(--border);
  cursor: pointer;
  transition: background 0.2s;
}

.history-item:hover {
  background: var(--bg-input);
}

.history-info {
  flex: 1;
}

.history-date {
  display: block;
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-bottom: 4px;
}

.history-values {
  font-size: 0.9rem;
}

.delete-btn {
  background: transparent;
  border: none;
  color: var(--text-secondary);
  font-size: 1.2rem;
  padding: 4px 8px;
  cursor: pointer;
}

.delete-btn:hover {
  color: var(--error);
}

.empty-msg {
  text-align: center;
  color: var(--text-secondary);
  padding: 20px;
  font-size: 0.9rem;
}

.app-footer {
  text-align: center;
  font-size: 0.8rem;
  color: var(--text-secondary);
  margin-top: 20px;
  margin-bottom: 40px;
}
</style>
